import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  ListSubheader,
  InputBase,
} from '@mui/material';
import {
  Business as BrandIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as ArrowDownIcon,
  SupervisorAccount as CurationIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../../context/BrandContext';
import { useAuth } from '../../../context/AuthContext';
import {
  operationalElevation,
  operationalFontSizes,
  operationalMotion,
  operationalRadii,
  operationalScrollbars,
  operationalSizing,
} from '../../../design-system/tokens';

const BrandSelector = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    allBrands,
    activeBrand,
    activeBrandData,
    loading,
    setActiveBrand,
    isImpersonating,
    canCurate
  } = useBrand();

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const open = Boolean(anchorEl);
  const selectorButtonSx = {
    minHeight: operationalSizing.default,
    borderRadius: `${operationalRadii.control}px`,
    px: 1.25,
    py: 0.5,
    minWidth: 'auto',
    textTransform: 'none',
    color: 'text.primary',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: isImpersonating ? 'warning.main' : 'divider',
    boxShadow: operationalElevation.none,
    transition: `background-color ${operationalMotion.quick}, border-color ${operationalMotion.quick}`,
    '&:hover': {
      bgcolor: 'action.hover',
      borderColor: 'text.secondary',
      boxShadow: operationalElevation.none
    },
    '& .MuiButton-endIcon': {
      ml: 0.75,
      color: 'text.secondary'
    }
  };
  const avatarSx = {
    width: 24,
    height: 24,
    borderRadius: `${operationalRadii.control}px`,
    bgcolor: 'action.selected',
    color: 'text.primary',
    fontSize: operationalFontSizes.xs,
    fontWeight: 700
  };
  const compactChipSx = {
    height: 20,
    borderRadius: operationalRadii.pill,
    bgcolor: 'action.selected',
    color: 'text.secondary',
    fontSize: '0.66rem',
    fontWeight: 700,
    '& .MuiChip-label': {
      px: 0.75
    }
  };
  const sectionLabelSx = {
    px: 2,
    py: 1,
    color: 'text.secondary',
    fontSize: operationalFontSizes.xs,
    fontWeight: 750,
    letterSpacing: 0,
    textTransform: 'none'
  };
  const menuItemSx = (selected = false) => ({
    mx: 1,
    my: 0.25,
    minHeight: 40,
    borderRadius: `${operationalRadii.control}px`,
    bgcolor: selected ? 'action.selected' : 'transparent',
    transition: `background-color ${operationalMotion.quick}`,
    '&:hover': {
      bgcolor: 'action.hover'
    },
    '&.Mui-selected': {
      bgcolor: 'action.selected',
      '&:hover': {
        bgcolor: 'action.selected'
      }
    }
  });

  // Separar marcas proprias das de clientes usando user._id
  // (para Admin, /api/brands retorna todas as marcas — nao serve para distinguir)
  const ownBrands = useMemo(() => {
    if (!allBrands.length) return [];
    if (!canCurate) return allBrands;
    return allBrands.filter(b => {
      const brandOwner = b.userId?._id || b.userId;
      return String(brandOwner) === String(user?._id);
    });
  }, [allBrands, user, canCurate]);

  // Agrupar marcas de clientes por usuario (ordenado alfabeticamente)
  const groupedClientBrands = useMemo(() => {
    if (!canCurate || !allBrands.length) return null;

    const ownIds = new Set(ownBrands.map(b => b._id));
    const otherBrands = allBrands.filter(b => !ownIds.has(b._id));

    if (otherBrands.length === 0) return null;

    const groups = {};
    otherBrands.forEach(brand => {
      const userId = brand.userId?._id || brand.userId || 'unknown';
      const userName = brand.userId?.name || 'Usuário';
      const userEmail = brand.userId?.email || '';
      if (!groups[userId]) {
        groups[userId] = { user: { _id: userId, name: userName, email: userEmail }, brands: [] };
      }
      groups[userId].brands.push(brand);
    });

    Object.values(groups).forEach(g => {
      g.brands.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
    });

    return Object.values(groups).sort((a, b) =>
      a.user.name.localeCompare(b.user.name, 'pt-BR', { sensitivity: 'base' })
    );
  }, [allBrands, ownBrands, canCurate]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setSearchTerm('');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectBrand = (brandId) => {
    setActiveBrand(brandId);
    handleClose();
  };

  const handleManageBrands = () => {
    handleClose();
    navigate('/brands');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!allBrands.length && !loading) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<ArrowDownIcon />}
        sx={selectorButtonSx}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Indicador de modo curadoria */}
          {isImpersonating && (
            <Chip
              icon={<CurationIcon sx={{ fontSize: 14 }} />}
              label="CURADORIA"
              size="small"
              sx={{
                ...compactChipSx,
                fontWeight: 700,
                border: '1px solid',
                borderColor: 'warning.main',
                color: 'text.primary',
                '& .MuiChip-icon': { ml: 0.5, color: 'warning.main' }
              }}
            />
          )}
          {activeBrandData?.logo ? (
            <Avatar
              src={activeBrandData.logo}
              sx={{ ...avatarSx, width: 22, height: 22 }}
            />
          ) : (
            <BrandIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary'
            }}
          >
            {activeBrandData?.name || 'Selecionar Marca'}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 420,
            maxHeight: 560,
            mt: 0.75,
            borderRadius: `${operationalRadii.surface}px`,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            boxShadow: operationalElevation.raised,
            overflow: 'hidden',
            '& .MuiList-root': {
              py: 0.75,
              maxHeight: 560,
              overflowY: 'auto',
              ...operationalScrollbars.thin
            },
            '& .MuiListItemIcon-root': {
              minWidth: 34
            }
          }
        }}
        MenuListProps={{
          dense: true
        }}
      >
        {/* Search Input Global */}
        {allBrands.length > 4 && (
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              minHeight: operationalSizing.compact,
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${operationalRadii.control}px`,
              px: 1.25,
              py: 0.25
            }}>
              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <InputBase
                placeholder="Buscar marca ou cliente..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                autoFocus
                sx={{ fontSize: operationalFontSizes.sm, flex: 1, color: 'text.primary' }}
              />
            </Box>
          </Box>
        )}

        {/* Minhas Marcas */}
        {(!searchTerm || ownBrands.filter(b => b.name.toLowerCase().includes(searchTerm.toLowerCase())).length > 0) && (
          <Box sx={sectionLabelSx}>
            <Typography variant="caption" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              Minhas Marcas {ownBrands.length > 0 && `(${ownBrands.length})`}
            </Typography>
          </Box>
        )}

        {ownBrands.length === 0 && !searchTerm ? (
          <MenuItem disabled sx={menuItemSx(false)}>
            <ListItemText primary="Nenhuma marca própria" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
          </MenuItem>
        ) : ownBrands
            .filter(b => b.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .map((brand) => (
          <MenuItem
            key={brand._id}
            onClick={() => handleSelectBrand(brand._id)}
            selected={activeBrand === brand._id && !isImpersonating}
            sx={menuItemSx(activeBrand === brand._id && !isImpersonating)}
          >
            <ListItemIcon>
              {brand.logo ? (
                <Avatar src={brand.logo} sx={avatarSx} />
              ) : (
                <BrandIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={brand.name}
              secondary={
                <>
                  {brand.userId?.name && (
                    <Typography component="span" variant="caption" display="block" sx={{ opacity: 0.8 }}>
                      {brand.userId.name}
                    </Typography>
                  )}
                  {brand.isDefault ? 'Padrao' : null}
                </>
              }
              primaryTypographyProps={{
                variant: 'body2',
                fontSize: operationalFontSizes.sm,
                fontWeight: activeBrand === brand._id && !isImpersonating ? 700 : 500
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                component: 'div'
              }}
            />
            {activeBrand === brand._id && !isImpersonating && (
              <CheckIcon sx={{ fontSize: 18, color: 'text.primary', ml: 1 }} />
            )}
            {brand.isDefault && activeBrand !== brand._id && (
              <Chip
                label="Padrao"
                size="small"
                sx={{ ...compactChipSx, ml: 1 }}
              />
            )}
          </MenuItem>
        ))}

        {/* Marcas de Clientes - Apenas para Admin/Mensure */}
        {canCurate && groupedClientBrands && groupedClientBrands.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />

            <Box sx={{
              ...sectionLabelSx,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CurationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
                  MODO CURADORIA — {groupedClientBrands.length} cliente{groupedClientBrands.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            {groupedClientBrands
              .map(group => {
                if (!searchTerm) return group;
                const lowerSearch = searchTerm.toLowerCase();
                if (group.user.name.toLowerCase().includes(lowerSearch) || (group.user.email && group.user.email.toLowerCase().includes(lowerSearch))) {
                  return group;
                }
                const filteredBrands = group.brands.filter(b => b.name.toLowerCase().includes(lowerSearch));
                if (filteredBrands.length > 0) {
                  return { ...group, brands: filteredBrands };
                }
                return null;
              })
              .filter(Boolean)
              .map((group, groupIndex, visibleGroups) => (
                <Box
                  key={group.user._id}
                  sx={{
                    pb: 0.5,
                    mb: 0.5,
                    borderBottom: groupIndex < visibleGroups.length - 1 ? '1px solid' : 0,
                    borderColor: 'divider'
                  }}
                >
                  <ListSubheader
                    sx={{
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      lineHeight: 1.2,
                      fontSize: operationalFontSizes.xs,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      mt: 0.25,
                      mb: 0.25
                    }}
                  >
                    <Avatar sx={{ ...avatarSx, width: 22, height: 22, borderRadius: operationalRadii.pill }}>
                      {group.user.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="caption" fontWeight={750} display="block" lineHeight={1.2}>
                        {group.user.name}
                      </Typography>
                      {group.user.email && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                          {group.user.email}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${group.brands.length} marca${group.brands.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ ...compactChipSx, ml: 'auto' }}
                    />
                  </ListSubheader>

                  {group.brands.map((brand) => (
                    <MenuItem
                      key={brand._id}
                      onClick={() => handleSelectBrand(brand._id)}
                      selected={activeBrand === brand._id && isImpersonating}
                      sx={{
                        ...menuItemSx(activeBrand === brand._id && isImpersonating),
                        pl: 5
                      }}
                    >
                      <ListItemIcon>
                        {brand.logo ? (
                          <Avatar src={brand.logo} sx={avatarSx} />
                        ) : (
                          <BrandIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={brand.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontSize: operationalFontSizes.sm,
                          fontWeight: activeBrand === brand._id && isImpersonating ? 700 : 500,
                          color: 'text.primary'
                        }}
                      />
                      {activeBrand === brand._id && isImpersonating && (
                        <CheckIcon sx={{ fontSize: 18, color: 'text.primary', ml: 1 }} />
                      )}
                    </MenuItem>
                  ))}
                </Box>
              ))
            }
          </>
        )}

        <Divider sx={{ my: 0.75 }} />

        <MenuItem onClick={handleManageBrands} sx={menuItemSx(false)}>
          <ListItemIcon>
            <SettingsIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText
            primary="Gerenciar Marcas"
            primaryTypographyProps={{ variant: 'body2', fontSize: operationalFontSizes.sm, fontWeight: 600 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default BrandSelector;
